import { observable, action, toJS } from "mobx";
import { WBSStore } from "modules/WBS/WBSStore";
import { WBSService } from "modules/WBS/WBSService";
import { CalculateWBSService } from "modules/WBS/CalculateWBSService";
import { WBSModels } from "modules/WBS/WBSModels";
import BaseStore from "../../common/BaseStore";
import { TakeOffStore } from "modules/takeOff/TakeOffStore";
import { WBSPropertiesStore } from "modules/WBS/WBSPropertiesStore";
import { ActivitySearchStore } from "modules/activitySearch/ActivitySearchStore";
import { ActivitySearchService } from "modules/activitySearch/ActivitySearchService";
import { CustomPropertyStore } from "modules/customProperties/CustomPropertyStore";
import { ERowType } from "common/enums";
import { IWBSRow } from "common/interfaces";
import { WBSRows } from "modules/WBS/models";
import { subtract, sum, times } from "utils/DecimalUtils";

type line = { index: number; content: any; origin: string };

class Store extends BaseStore {
  WBSCuttedLine: line | undefined;
  WBSCopiedLine: any;
  @observable isProcessing: boolean = false;
  @observable disableBar: boolean = false;
  @observable undoList: Array<any> = [];
  @observable idOldCP: Array<any> = [];
  valores: any;

  constructor() {
    super("ToolbarStore");
  }

  resetStore() {
    this.WBSCopiedLine = undefined;
    this.WBSCuttedLine = undefined;
    this.isProcessing = false;
    this.undoList = [];
  }

  /** Define se os botões da barra de ferramentas devem estar desabilitados.
   * Altera propriedade disableBar na Store. */
  @action
  changeBarStatus = (status: boolean = false) => {
    this.disableBar = status;
  };

  isDisabled = (verifyFunction?) => {
    let index = WBSStore.selectedLine.index;
    if (
      WBSStore.selectedLine &&
      WBSStore.selectedLine.data &&
      WBSStore.selectedLine.data.modelMapFull
    ) {
      index = this.findModelMapMainLineIndex(WBSStore.selectedLine);
    }
    return ToolbarStore.isProcessing
      ? true
      : index === -1
      ? true
      : WBSStore.tab === 1
      ? true
      : WBSStore.isLoadingOperations === true
      ? true
      : verifyFunction
      ? verifyFunction()
      : false;
  };

  pasteVerify = () => !(this.WBSCuttedLine || this.WBSCopiedLine);

  replicateVerify = () => {
    let lista = WBSStore.dataTable,
      resp = true;
    lista.forEach((l) => {
      if (
        WBSStore.selectedLine.id === l.data.parentStage &&
        l.data.rowType !== 0
      ) {
        resp = false;
      }
    });
    return resp;
  };

  separateVerify = () => {
    let idx = -1;
    if (WBSStore.selectedLine.data.modelMapFull) {
      idx = this.findModelMapMainLineIndex(WBSStore.selectedLine);
    }

    let data: any = undefined;

    if (idx != -1 && WBSStore.dataTable[idx]) {
      data = WBSStore.dataTable[idx].data;
    } else {
      data = WBSStore.selectedLine.data;
    }

    let index = idx != -1 ? idx : WBSStore.selectedLine.index;

    if (data == undefined) return true;
    if (data.rowType === ERowType.BLANK) return true;
    if (data.parentStage === -1) return true;
    if (data.rowIndex === 0 && data.parentStage === -1) return true;
    if (
      index !== 0 &&
      WBSStore.dataTable[index - 1] &&
      WBSStore.dataTable[index - 1].data.rowType === ERowType.BLANK
    )
      return true;

    if (data.modelMapFull == true) {
      let count = WBSStore.dataTable.filter(
        (d) =>
          d.data.parentStage == data.parentStage &&
          d.data.rowIndex > data.rowIndex
      );
      if (count.length > 0) {
        return true;
      }
    }

    return false;
  };

  agroupVerify = () => {
    let idx = -1;
    if (WBSStore.selectedLine.data.modelMapFull) {
      idx = this.findModelMapMainLineIndex(WBSStore.selectedLine);
    }
    let data: any = undefined;
    if (idx != -1 && WBSStore.dataTable[idx]) {
      data = WBSStore.dataTable[idx].data;
    } else {
      data = WBSStore.selectedLine.data;
    }

    if (data == undefined) return true;
    if (data.rowType === ERowType.BLANK) return true;
    if (data.rowIndex === 0) return true;

    // Se a linha acima for model map e elas estiverem na mesma posição, não agrupar
    let parentModel = WBSStore.WBSRows.find(
      (r) =>
        r.data.fromModelMap == undefined &&
        r.data.rowIndex == data.rowIndex - 1 &&
        r.data.parentStage == data.parentStage &&
        r.data.rowType != ERowType.BLANK
    );
    if (parentModel) {
      return false;
    } else {
      return true;
    }
  };

  moveupVerify = () => {
    let idx = -1;
    if (WBSStore.selectedLine.data.modelMapFull) {
      idx = this.findModelMapMainLineIndex(WBSStore.selectedLine);
    }
    let data: any = undefined;
    if (idx != -1) {
      if (WBSStore.dataTable[idx]) {
        data = WBSStore.dataTable[idx].data;
      }
    } else {
      data = WBSStore.selectedLine.data;
    }
    if (data === undefined) return true;
    if (data.rowIndex === 0) return true;
    return false;
  };

  formulaVerify = () => {
    if (WBSStore.selectedLine.data.fromModelMap) {
      return true;
    }
    return false;
  };

  movedownVerify = () => {
    let idx = -1;
    if (WBSStore.selectedLine.data.modelMapFull) {
      idx = this.findModelMapMainLineIndex(WBSStore.selectedLine);
    }
    try {
      let data: any = undefined;
      if (idx != -1 && WBSStore.dataTable[idx]) {
        data = WBSStore.dataTable[idx].data;
      } else {
        data = WBSStore.selectedLine.data;
      }
      if (data == undefined) return true;
      let WBSRowsIndex =
        idx != -1
          ? WBSStore.dataTable[idx].WBSRowsIndex
          : WBSStore.selectedLine.WBSRowsIndex;
      if (WBSStore.WBSRows[WBSRowsIndex] == undefined) return false;
      let index = WBSStore.WBSRows.findIndex(
        (r) =>
          r.data.parentStage == data.parentStage &&
          r.data.rowIndex == data.rowIndex + 1
      );
      if (index == -1) return true;
    } catch (error) {
      return true;
    }
    return false;
  };

  getParent(id, index) {
    let parentRef,
      parentLastLevelRow,
      parent,
      parentIndex = -1;
    if (id === "") {
      parentRef = WBSStore.WBSRef;
      (parentLastLevelRow = WBSStore.dataTable.length - 1),
        (parent = WBSStore.WBS);
    } else {
      // parentRef = WBSStore.WBSRef.collection('wbsRows').doc(id);
      // parentIndex = WBSStore.rowCods[index].parent.index;
      // parentLastLevelRow = WBSStore.foundLastLevelRow(parentIndex);
      // parent = WBSStore.dataTable[parentIndex].row;
    }
    return { parentRef, parentLastLevelRow, parent, parentIndex };
  }

  createLocationInActivity(path, location, content) {
    let locationRef = WBSService.getDocRef(path).collection("locations").doc();
    return {
      type: "set",
      args: [locationRef, location],
      arrayChange: () => {
        content.data.locations.push(
          WBSModels.locations(WBSStore.getModel(locationRef, location))
        );
      },
    };
  }

  deleteChildrenLocation({ row, content }, collection, actions) {
    // As locations são indefinidas se estiver tentando excluir uma linha vazia.
    if (content.data.locations === undefined) return;
    let index = content.data.locations.findIndex(
      (item) => item.data.id === row.id && item.data.parent === collection
    );
    if (index !== -1)
      actions.push({
        type: "delete",
        args: [
          WBSService.getDocRef(row.data.contentReference.path)
            .collection("locations")
            .doc(content.data.locations[index].id),
        ],
        arrayChange: () => {
          let index = content.data.locations.findIndex(
            (item) => item.data.id === row.id && item.data.parent === collection
          );
          content.data.locations.splice(index, 1);
        },
      });
  }

  /** Deleta a linha selecionada, se houver. */
  private async deleteWBSRowSelected(
    deleteModelMapRow = false,
    updateRowIndex = true
  ) {
    if (WBSStore.selectedLine) {
      return this.deleteWBSRowById(
        WBSStore.selectedLine.id,
        deleteModelMapRow,
        updateRowIndex
      );
    }
  }

  /** Deleta a linha informada por parâmetro e seu contéudo em caso de etapas ou composições.
   * Remove os vínculos necessários em caso de model-map e ajusta os dados de linha irmãs e pai, se houver.
   */
  async deleteWBSRowById(
    rowId: string,
    deleteModelMapRow = false,
    updateRowIndex = true
  ) {
    let rowToDelete = WBSStore.dataTable.find((row) => row.id === rowId);
    if (!deleteModelMapRow) {
      rowToDelete = await this.removeLinkAndFindModelMapRow(rowToDelete);
    }

    if (!rowToDelete || !rowToDelete.data)
      throw new Error("Row to delete not found!");
    rowId = rowToDelete.id;

    let actions = new Array();
    WBSStore.loadDatatableWithRightFormat(WBSStore.WBSRows);

    // Variavel responsavel para verificar se a linha acima é stage e não tem outros filhos
    // Caso isso ocorra, a linha acima deve ser transformada em resource
    const { parentStage } = rowToDelete.data;
    const hasToChangeStage =
      parentStage && parentStage != -1
        ? WBSStore.findRowSiblings(rowId, parentStage)
        : [];

    actions = this.updateParentsAndSiblingsRows(
      rowToDelete.data,
      actions,
      updateRowIndex
    );

    actions = this.deleteRowAndContent(rowToDelete, actions);

    if (hasToChangeStage.length == 0 && !deleteModelMapRow) {
      const { parentStage } = rowToDelete.data;
      actions = this.setStageRowToResource(String(parentStage), actions);
    }

    await WBSStore.batchActions(
      actions,
      TakeOffStore.statusModelmap != "Loading" && WBSStore.loading == false,
      () => {
        if (deleteModelMapRow == false) {
          WBSStore.loadDatatableWithRightFormat(WBSStore.WBSRows);

          let selectedIdx = WBSStore.dataTable.findIndex((r) => r.id === rowId);
          if (selectedIdx !== -1) {
            if (selectedIdx == WBSStore.dataTable.length) {
              WBSStore.setSelectedLine(WBSStore.dataTable.length - 1);
            }
          }
        }
      }
    );
    const index = WBSStore.selectedLine.index;
    WBSStore.setSelectedLine(
      index === 0 ? index + 2 : index - 1,
      undefined,
      true
    ); // Seleciona  a linha abaixo ou acima para limpar o códico
    WBSStore.setSelectedLine(index, undefined, true); // seleciona a linha correta
    ToolbarStore.updateLastSelectedLineOfUndoList();
  }

  /** Remove o vínculo do plano de custo com a EAP e retorna a linha principal
   * no caso de um model-map full.
   */
  private async removeLinkAndFindModelMapRow(
    row: IWBSRow | undefined
  ): Promise<IWBSRow | undefined> {
    if (row && row.data) {
      const { fromModelMap } = row.data;
      if (fromModelMap) {
        await WBSStore.removeCostPlanLink(fromModelMap, true);
        const headerRow = this.findModelMapHeaderRow(row);
        if (headerRow) return headerRow;
      }
    }
    return row;
  }

  private updateParentsAndSiblingsRows(
    row: WBSRows,
    actions: any[],
    updateRowIndex: boolean
  ) {
    if (updateRowIndex) {
      const { rowIndex, parentStage } = row;
      actions.push(...this.changeRowIndex(rowIndex + 1, parentStage, -1));
    }

    const { total, totalBdi, parentStage, parentTask } = row;
    if (total != undefined && total != 0) {
      actions.push(
        ...WBSStore.createActionsToUpdateParentRows(
          parentStage ? parentStage : parentTask,
          -(total || 0),
          -(totalBdi || 0)
        )
      );
    }

    return actions;
  }

  private deleteRowAndContent(rowToDelete: IWBSRow, actions: any[]) {
    const rowId = rowToDelete.id;
    const docRef = WBSStore.WBSRef.collection("rows").doc(rowId);

    WBSStore.deleteAllArray(rowToDelete);
    actions.push({ type: "delete", args: [docRef] });

    const { rowType } = rowToDelete.data;
    if (rowType == ERowType.STAGE || rowType == ERowType.TASK) {
      // Deleta recursivamente as linhas de dentro da task/stage
      actions.push(...WBSStore.recursiveRowDelete(rowId));
    }

    return actions;
  }

  private setStageRowToResource(rowId: string, actions: any[]) {
    const rowToChange = WBSStore.WBSRows.find((r) => r.id == rowId);
    if (rowToChange && rowToChange.data) {
      const docRef = WBSStore.WBSRef.collection("rows").doc(rowToChange.id);
      const newRow = WBSModels.getDefaultRow({
        rowType: ERowType.RESOURCE,
        rowIndex: rowToChange.data.rowIndex,
        description: rowToChange.data.description,
        total: 0,
      });
      newRow.parentStage = rowToChange.data.parentStage;

      const updatedData = WBSService.removeUndefinedFieldsFromUpdatedRow(
        rowToChange,
        newRow
      );
      newRow.id = rowToChange.id;

      for (const key of Object.keys(updatedData)) {
        if (!Object.keys(newRow).includes(key)) {
          newRow[key] = undefined;
        }
      }

      actions.push({
        type: "update",
        args: [docRef, updatedData],
      });
    }

    return actions;
  }

  // Atualiza os valores para o usuário
  updateValues(response) {
    let resources = [...WBSStore.resources];
    let tasks = [...WBSStore.tasks];

    for (let i = 0; i < response.data.tasks.length; i++) {
      for (let j = tasks.length - 1; j >= 0; j--) {
        if (tasks[j].id == response.data.tasks[i]) {
          tasks.splice(j, 1);
          continue;
        }
      }
    }
    for (let i = 0; i < response.data.resources.length; i++) {
      for (let j = resources.length - 1; j >= 0; j--) {
        if (resources[j].id == response.data.resources[i]) {
          resources.splice(j, 1);
          continue;
        }
      }
    }
    WBSStore.resources = resources;
    WBSStore.tasks = tasks;
  }

  /** Executa a ação e monta os objetos necessários para a ação de
   * desfazer.
   */
  @action
  async handelActions(params) {
    let { originalOperation, undoOperation, data } = params;

    // Quando exede o número de ações de desfazer permitira exclui o item que está a mais tempo na lista.[Funcionamento de lista]
    if (this.undoList.length >= 5 && originalOperation !== "undoWBSRow") {
      this.undoList.pop();
    }

    if (originalOperation !== "undoWBSRow") {
      if (data.undoFunction.selectedLine.length > 0) {
        this.undoList.unshift({
          originalOperation: originalOperation,
          undoOperation: undoOperation,
          parameter: {
            undoFunction: {
              ...data.undoFunction,
              selectedLine: data.undoFunction.selectedLine[0],
            },
            originalFunction: data.originalFunction,
          },
        });
      } else {
        this.undoList.unshift({
          originalOperation: originalOperation,
          undoOperation: undoOperation,
          parameter: {
            undoFunction: {
              ...data.undoFunction,
              selectedLine: WBSStore.selectedLine,
            },
            originalFunction: data.originalFunction,
          },
        });
      }
    }
  }

  /**
   * Esta função atualiza o objeto selectedLine da última ação adicionada,
   * para os casos em que a linha selecionada é alterada dentro da função ultilizada de forma assíncrona.
   * Como por exemplo no caso da função createWBSResource.
   */
  @action
  updateLastSelectedLineOfUndoList() {
    if (!this.undoList || !this.undoList.length) return;

    if (WBSStore.selectedLine) {
      const lastAction = this.undoList[0];
      lastAction.parameter.undoFunction.selectedLine = WBSStore.selectedLine;
    }
  }

  @action
  clearUndoList() {
    this.undoList = [];
  }

  @action
  async undoWBSRow() {
    // Quando a função de desfazer é acionada elimina o item que está a menos tempo na lista.[Funcionamento de pilha]
    let undo = this.undoList.shift();
    const fn = undo.parameter.undoFunction.function;
    let param = undo.parameter.undoFunction.params;
    const line = undo.parameter.undoFunction.selectedLine;
    await WBSStore.setSelectedLine(line.index);
    // console.log("================================================")
    // console.log("Ação à desfazer ===> ", undo)
    // console.log("================================================")
    switch (fn) {
      case "deleteWBSRow":
        await ToolbarStore.deleteWBSRow();
        break;

      case "reverseWBSResourceToBlank":
        if (param) {
          WBSStore.reverseWBSResourceToBlank(param[0]);
        }
        break;

      case "moveWBSRow":
        if (param) {
          ToolbarStore.moveWBSRow(param[0]);
        } else {
          ToolbarStore.moveWBSRow();
        }
        break;

      case "changeWBSRowLevel":
        let originalParam = undo.parameter.originalFunction.params;
        if (originalParam[0]) {
          this.undoBack(param, line);
        } else {
          this.undoFront(param, line);
        }
        break;

      case "pasteWBSRow":
        const copiedLine = undo.parameter.undoFunction.copiedLine;
        if (copiedLine.row.data.rowType === 1) {
          if (
            copiedLine.row.data.rateLibraryCode &&
            copiedLine.row.data.rateLibraryId
          ) {
            const rateLibraryTaskId =
              copiedLine.row.data.rateLibraryId +
              "-T" +
              copiedLine.row.data.rateLibraryCode;
            let taskItem = await ActivitySearchService.getRateLibraryTaskById(
              rateLibraryTaskId
            );
            await ActivitySearchStore.handleSelected(taskItem, "RL");
          } else {
            ToolbarStore.undoPasteWBSRow(copiedLine);
          }
        } else {
          ToolbarStore.undoPasteWBSRow(copiedLine);
        }
        break;

      case "changeSelectedLineCode":
        WBSStore.changeSelectedLineCode(param[0], param[1]);
        break;

      case "applyFormula":
        await WBSPropertiesStore.applyFormula(param[0], param[1]);
        if (param[0] === undefined || param[0] === "") {
          await WBSStore.updateWBSRow(line, line.index, "quantity", param[2]);
        }
        break;

      case "changeSelectedLineCategory":
        WBSStore.changeSelectedLineCategory(param[0]);
        break;

      case "updateWBSRow":
        await WBSStore.setSelectedLine(param[1]);
        WBSStore.updateWBSRow(
          param[0],
          param[1],
          param[2],
          param[3],
          "totalBdi",
          WBSStore.selectedLine.data.totalBdi
        );
        break;

      case "recursiveUpdateWBSSubcollection":
        WBSStore.recursiveUpdateWBSSubcollection(param[0], param[1]);
        break;

      case "updateWBSSubcollection":
        WBSStore.updateWBSSubcollection(param[0], param[1], param[2]);
        break;

      case "removeQuantityLinkWithTakeOffItem":
        await TakeOffStore.removeQuantityLinkWithTakeOffItem(param[0]);
        break;

      case "addQuantityFromTakeOffItem":
        await TakeOffStore.addQuantityFromTakeOffItem(param[0]);
        break;

      case "syncCustomProperty":
        await CustomPropertyStore.undoSyncCustomProperty(
          param[0],
          param[1],
          param[2]
        );
        break;

      case "unlinkComposition":
        await WBSService.unlinkComposition(param[0], param[1]);
        break;

      default:
        break;
    }
  }

  async undoFront(param, line) {
    let actions = new Array();
    let arr: [any] = param;
    let index = arr.findIndex((a) => a.id == line.id);

    await WBSStore.setSelectedLine(arr[index], "description");
    await ToolbarStore.changeWBSRowLevel("forward");
    let shouldTransform = false;
    // Precisa recriar
    if (arr[index].data.rowType != ERowType.STAGE) {
      let allSameParentIdArray = arr.filter(
        (a) => a.data.parentStage == arr[index].id
      );

      for (let i = 0; i < allSameParentIdArray.length; i++) {
        const element = allSameParentIdArray[i];
        if (element.index <= arr[index].index) {
          continue;
        }
        shouldTransform = true;
        await WBSStore.setSelectedLine(element.index, "description");
        await ToolbarStore.changeWBSRowLevel();
      }

      let changeRow = WBSStore.WBSRef.collection("rows").doc(arr[index].id);

      let indexNewRow = WBSStore.WBSRows.findIndex(
        (r) => r.id == arr[index].id
      );

      arr[index].data.parentStage =
        WBSStore.WBSRows[indexNewRow].data.parentStage;
      arr[index].data.rowIndex = WBSStore.WBSRows[indexNewRow].data.rowIndex;

      let update = WBSService.removeUndefinedFieldsFromUpdatedRow(
        arr[index],
        arr[index].data
      );
      actions.push({
        type: "update",
        args: [changeRow, update],
      });

      if (shouldTransform) {
        actions.push(
          ...WBSStore.createActionsToUpdateParentRows(
            arr[index].data.parentStage,
            arr[index].data.total,
            arr[index].data.totalBdi
          )
        );
      }

      await WBSStore.batchActions(actions, true, () => {
        WBSStore.loadDatatableWithRightFormat(WBSStore.WBSRows);
      });
    }
  }

  async undoBack(param, line) {
    let actions = new Array();
    let arr: [any] = param;
    let index = arr.findIndex((a) => a.id == line.id);

    let selectedLineRef = WBSStore.WBSRef.collection("rows").doc(arr[index].id);

    // Muda a linha selecionada para stage
    let update = WBSService.removeUndefinedFieldsFromUpdatedRow(
      line,
      arr[index].data
    );
    actions.push({
      type: "update",
      args: [selectedLineRef, update],
    });

    let allSameParentIdArray = arr.filter(
      (a) => a.data.parentStage == arr[index].id
    );
    for (let i = 0; i < allSameParentIdArray.length; i++) {
      const element = allSameParentIdArray[i];

      let childRef = WBSStore.WBSRef.collection("rows").doc(element.id);

      actions.push({
        type: "update",
        args: [
          childRef,
          {
            parentStage: element.data.parentStage,
            rowIndex: element.data.rowIndex,
          },
        ],
      });
    }

    // recria a linha acima
    let indexUpper = arr.findIndex((a) => a.id == line.data.parentStage);
    if (indexUpper != -1) {
      // Puxa as linhas para cima
      actions.push(
        ...this.changeRowIndex(
          arr[indexUpper].data.rowIndex + 1,
          arr[indexUpper].data.parentStage,
          +1
        )
      );
      let selectedLineUpperRef = WBSStore.WBSRef.collection("rows").doc(
        arr[indexUpper].id
      );
      let updateLineUpper = WBSService.removeUndefinedFieldsFromUpdatedRow(
        arr[indexUpper],
        arr[indexUpper].data
      );

      actions.push({
        type: "update",
        args: [selectedLineUpperRef, updateLineUpper],
      });

      if (arr[indexUpper].data.rowType == ERowType.TASK) {
        actions.push(...this.recursiveAdd(arr[indexUpper].id, param));
      }
      actions.push(
        ...WBSStore.createActionsToUpdateParentRows(
          arr[indexUpper].data.parentStage,
          arr[indexUpper].data.total,
          arr[indexUpper].data.totalBdi
        )
      );
    } else {
      // Puxa as linhas para cima
      actions.push(
        ...this.changeRowIndex(arr[index].data.rowIndex + 1, -1, +1)
      );
    }

    await WBSStore.batchActions(actions, true, () => {
      WBSStore.loadDatatableWithRightFormat(WBSStore.WBSRows);
    });
  }

  recursiveAdd(id, data) {
    let actions: any = [];
    for (let i = 0; i < data.length; i++) {
      const element = data[i];
      if (element.data.parentTask == id) {
        let selectedLineUpperRef = WBSStore.WBSRef.collection("rows").doc(
          element.id
        );
        let defaultRow: any = {};
        if (element.data.rowType == ERowType.BLANK) {
          defaultRow = WBSStore.getBlankRow(element.data.rowIndex);
          defaultRow.parentTask = element.data.parentTask;
        } else {
          defaultRow = WBSModels.getDefaultRow({ ...element.data });
          defaultRow.parentTask = element.data.parentTask;
        }
        actions.push({
          type: "set",
          args: [selectedLineUpperRef, defaultRow],
        });

        if (element.data.rowType == ERowType.TASK) {
          actions.push(...this.recursiveAdd(element.id, data));
        }
      }
    }
    return actions;
  }

  @action
  async cutWBSRow() {
    let selectedLine = {
      ...WBSStore.WBSRows[WBSStore.selectedLine.WBSRowsIndex],
    };
    this.WBSCopiedLine = {
      row: selectedLine,
      data: WBSStore.recursiveRowGet(selectedLine.id),
    };
    await this.deleteWBSRowSelected();
  }

  @action
  async copyWBSRow(activity?) {
    if (!activity) {
      if (WBSStore.selectedLine.data.modelMapFull) {
        let idx = this.findModelMapMainLineIndex(WBSStore.selectedLine);
        await WBSStore.setSelectedLine(idx);
      }
    }

    let selectedLine: any = activity
      ? { ...activity }
      : {
          ...WBSStore.WBSRows.find(
            (r) => r.WBSRowsIndex == WBSStore.selectedLine.WBSRowsIndex
          ),
        };
    this.WBSCopiedLine = {
      row: selectedLine,
      data: WBSStore.recursiveRowGet(selectedLine.id),
    };
  }

  @action
  async replicateWBSRow() {
    WBSStore.replicateModal = true;
  }

  async pasteRecursive(
    row: { data; index },
    data: [],
    first?,
    parentReference?,
    isActivity?,
    isModelMap?,
    insertInsideModelMapFull?
  ) {
    if (
      WBSStore.selectedLine.data.modelMapFull &&
      insertInsideModelMapFull == undefined
    ) {
      let idx = this.findModelMapMainLineIndex(WBSStore.selectedLine);
      await WBSStore.setSelectedLine(idx);
    }

    if (isModelMap) {
      row.data.quantity = isModelMap.quantity;
      row.data.total = isModelMap.total;
    }

    let selectedLine = isModelMap
      ? { data: isModelMap, index: -1, id: "" }
      : WBSStore.selectedLine;
    let actions = new Array();

    if (first) {
      if (insertInsideModelMapFull) {
        await ToolbarStore.deleteWBSRow(true);
        row.data.rateLibraryName = row.data.description;
        row.data.total = times(
          row.data.unitCost,
          selectedLine.data.quantity,
          WBSPropertiesStore.getRules(2)
        );
        row.data.quantity = selectedLine.data.quantity;
        row.data.modelMapDescription = selectedLine.data.modelMapDescription;
        row.data.fromModelMap = selectedLine.data.fromModelMap;
        row.data.modelMapFull = true;
        row.data.description = selectedLine.data.description;
      } else {
        if (isActivity) {
          row.data.quantity = 0;
          row.data.total = row.data.unitCost * row.data.quantity;

          const globalBDI = WBSStore.WBS.data.bdi.percentual;
          const result = CalculateWBSService.calculateRowBdi(
            row.data,
            row.data.unitCost,
            globalBDI,
            WBSPropertiesStore.getRules()
          );
          row.data.totalBdi = result ? result.totalSale : 0;
          row.data.vendaBdi = result ? result.unitCostSale : 0;
        }
      }
      /**
       *  PRUBK-967: A atribuição do rowIndex foi tasido para a linha antes de reordenar as linhas,
       *  pois ele estava recebendo o ultimo valor do selectedLine.data.rowIndex
       */
      row.data.rowIndex = selectedLine.data.rowIndex;
      if (!isModelMap && selectedLine.data.rowType != ERowType.BLANK) {
        // Puxa as linhas para baixo
        actions.push(
          ...this.changeRowIndex(
            selectedLine.data.rowIndex,
            selectedLine.data.parentStage
          )
        );
      }
      // row.data.rowIndex = selectedLine.data.rowIndex; // essa linha foi passada para a linha 371
      row.data.parentStage = selectedLine.data.parentStage;
      row.index = selectedLine.index;
    }
    if (parentReference) {
      if (row.data.parentStage) {
        row.data.parentStage = parentReference;
      } else {
        row.data.parentTask = parentReference;
      }
    }
    if (first && !isModelMap) {
      let wbs = WBSStore.WBSRows.find((r) =>
        r.id == row.data.parentStage
          ? row.data.parentStage
          : row.data.parentTask
      );
      if (wbs) {
        // atualiza o total das linhas acima recursivamente
        let newData: any = await WBSStore.recursiveUpdateWBSSubcollection(
          wbs.data.contentId,
          row.data.rowType == ERowType.STAGE
            ? row.data.total
            : sum(
                times(
                  row.data.unitCost,
                  row.data.quantity,
                  WBSPropertiesStore.getRules(2)
                ),
                wbs.data.total || 0
              ),
          wbs.data.total,
          row.data.parentStage ? row.data.parentStage : row.data.parentTask,
          true,
          row.data.totalBdi
        );
        actions.push(...newData);
      } else {
        actions.push(
          WBSStore.setNewWbsTotal(row.data.total, row.data.totalBdi)
        );
      }
    }

    // Linha pai
    let rowRef: any = undefined;
    let rowRefNewId: any = "";
    let contentIdToDelete = "";
    if (selectedLine.data.rowType == ERowType.BLANK && !isModelMap) {
      rowRef = WBSStore.WBSRef.collection("rows").doc(selectedLine.id);
      rowRefNewId = selectedLine.id;
      // PRUBK-967: As duas linhas a baixo estavam comentadas, descomentando foltou a funcionar
      let update = {
        data: { ...row.data },
        id: rowRefNewId,
        WBSRowsIndex: WBSStore.WBSRows.length,
      };
      WBSStore.changeAllArray({ ...row.data }, selectedLine, false);
      delete row.data.tree;
      contentIdToDelete = row.data.contentId ? row.data.contentId : "";
      actions.push({
        type: "update",
        args: [rowRef, row.data],
      });
    } else {
      rowRef = WBSStore.WBSRef.collection("rows").doc();
      rowRefNewId = rowRef.id;
      delete row.data.tree;
      let update = {
        data: { ...row.data },
        id: rowRefNewId,
        WBSRowsIndex: WBSStore.WBSRows.length,
      };
      WBSStore.WBSRows[WBSStore.WBSRows.length] = { ...update };
      contentIdToDelete = row.data.contentId ? row.data.contentId : "";
      actions.push({
        type: "set",
        args: [rowRef, { ...row.data }],
      });
    }

    if (contentIdToDelete != "") {
      actions.push({
        type: "delete",
        args: [WBSStore.WBSRef.collection("resources").doc(contentIdToDelete)],
      });
    }

    // Linhas subsequentes
    for (let i = 0; i < data.length; i++) {
      let dataRow = JSON.parse(JSON.stringify(data[i]));
      if (dataRow.row.data.parentTask) {
        dataRow.row.data.parentTask = rowRefNewId;
      } else {
        dataRow.row.data.parentStage = rowRefNewId;
      }
      delete dataRow.row.data.tree;
      rowRef = WBSStore.WBSRef.collection("rows").doc();
      WBSStore.WBSRows[WBSStore.WBSRows.length] = {
        data: { ...dataRow.row.data },
        id: rowRef.id,
        WBSRowsIndex: WBSStore.WBSRows.length,
      };
      for (let i = 0; i < dataRow.data.length; i++) {
        let recursive = await this.pasteRecursive(
          dataRow.data[i].row,
          dataRow.data[i].data,
          undefined,
          rowRef.id
        );
        actions.push(...recursive);
      }

      contentIdToDelete = dataRow.row.data.contentId
        ? dataRow.row.data.contentId
        : "";
      if (contentIdToDelete != "") {
        actions.push({
          type: "delete",
          args: [
            WBSStore.WBSRef.collection("resources").doc(contentIdToDelete),
          ],
        });
      }
      actions.push({
        type: "set",
        args: [rowRef, { ...dataRow.row.data }],
      });
    }

    if (first) {
      await WBSStore.batchActions(actions, true, () => {
        WBSStore.loadDatatableWithRightFormat(WBSStore.WBSRows);
        WBSStore.resetSelectedLine();
        WBSStore.setSelectedLine(selectedLine.index, "description");
      });
    }
    return actions;
  }

  @action
  async pasteWBSRow(isActivity?, isModelMap?, insertInsideModelMapFull?) {
    await this.pasteRecursive(
      JSON.parse(JSON.stringify(this.WBSCopiedLine.row)),
      JSON.parse(JSON.stringify(this.WBSCopiedLine.data)),
      true,
      undefined,
      isActivity,
      isModelMap,
      insertInsideModelMapFull
    );
    WBSStore.updateResource = true;
  }

  @action
  async undoPasteWBSRow(
    copiedLine,
    isActivity?,
    isModelMap?,
    insertInsideModelMapFull?
  ) {
    await this.pasteRecursive(
      JSON.parse(JSON.stringify(copiedLine.row)),
      JSON.parse(JSON.stringify(copiedLine.data)),
      true,
      copiedLine.row.parentStage,
      isActivity,
      isModelMap,
      insertInsideModelMapFull
    );
    WBSStore.updateResource = true;
  }

  @action
  async createBlankWBSRow(position = "above", newSelectedIndex?, delay = true) {
    if (
      WBSStore.selectedLine.data &&
      WBSStore.selectedLine.data.modelMapFull &&
      !newSelectedIndex
    ) {
      let idx = this.findModelMapMainLineIndex(WBSStore.selectedLine);
      await WBSStore.setSelectedLine(idx);
    }
    let selectedLine = newSelectedIndex
      ? newSelectedIndex
      : WBSStore.selectedLine;
    let rowIndex =
      position === "bellow"
        ? selectedLine.data.rowIndex + 1
        : selectedLine.data.rowIndex;
    let blankRow: any = WBSStore.getBlankRow(rowIndex);
    blankRow.parentStage = selectedLine.data.parentStage;
    let actions = new Array();
    let newRowRef = await WBSStore.WBSRef.collection("rows").doc();

    actions.push(...this.changeRowIndex(rowIndex, blankRow.parentStage));

    WBSStore.WBSRows[WBSStore.WBSRows.length] = {
      data: blankRow,
      id: newRowRef.id,
      WBSRowsIndex: WBSStore.WBSRows.length,
    };
    actions.push({
      type: "set",
      args: [newRowRef, blankRow],
    });

    WBSStore.loadDatatableWithRightFormat(WBSStore.WBSRows);
    let index = WBSStore.dataTable.findIndex(
      (d) =>
        d.data.rowIndex == rowIndex &&
        d.data.parentStage == blankRow.parentStage
    );

    await WBSStore.batchActions(actions, true, () => {
      setTimeout(() => {
        WBSStore.setSelectedLine(index + 1, "description", true); // Seleciona  a linha abaixo para que o campo description seja limpo
        WBSStore.setSelectedLine(index, "description", true); // seleciona a linha correta
        this.updateLastSelectedLineOfUndoList();
      }, 300);
    });
    return newRowRef.id;
  }

  changeRowIndex(
    rowIndex,
    parentStage,
    direction = 1,
    newParentStage: any = undefined
  ) {
    let actions = new Array();
    let dt = toJS(WBSStore.WBSRows);
    let parentStageId = parentStage.id ? parentStage.id : parentStage;
    let filtred = dt.filter(
      (dtRow) =>
        dtRow.data.rowIndex >= rowIndex &&
        dtRow.data.parentStage == parentStageId
    );

    for (let i = 0; i < filtred.length; i++) {
      let update: any = { rowIndex: filtred[i].data.rowIndex + direction };
      if (newParentStage != undefined) {
        update.parentStage = newParentStage;
      }
      WBSStore.changeAllArray(update, filtred[i], true);
      actions.push({
        type: "update",
        args: [WBSStore.WBSRef.collection("rows").doc(filtred[i].id), update],
      });
    }
    return actions;
  }

  /** Deleta a linha selecionada e ativa a flag para atualizar
   * os dados na EAP caso o loading não esteja ativo.
   */
  @action
  async deleteWBSRow(deleteModelMapRow = false, updateRowIndex = true) {
    await this.deleteWBSRowSelected(deleteModelMapRow, updateRowIndex);
    if (TakeOffStore.statusModelmap != "Loading" && WBSStore.loading == false) {
      if (!WBSStore.updateResource) {
        WBSStore.updateResource = true;
      }
    }
  }

  /** Procura a linha princiapal das linhas do model-map full. */
  findModelMapHeaderRow(first: IWBSRow): IWBSRow | undefined {
    let line: any = first;
    let tempLine: any = undefined;
    while (line && line.data && line.data.fromModelMap != undefined) {
      tempLine = WBSStore.dataTable.find(
        (dt) => dt.id == line.data.parentStage
      );
      if (
        tempLine == undefined ||
        tempLine.data == undefined ||
        tempLine.data.fromModelMap == undefined
      ) {
        break;
      }
      line = tempLine;
    }

    return line;
  }

  findModelMapMainLineIndex(first): number {
    let line: any = first;
    if (!line || !line.data) return -1;
    let index = -1;
    do {
      index = line.index;
      line = WBSStore.dataTable.find((dt) => dt.id == line.data.parentStage);
      if (!line || !line.data) break;
    } while (line.data.fromModelMap);

    return index;
  }

  @action
  async changeWBSRowLevel(to = "back") {
    // Flag para atualizar listagem de insumos
    if (!WBSStore.updateResource) {
      WBSStore.updateResource = true;
    }

    if (WBSStore.selectedLine.data.modelMapFull) {
      let idx = this.findModelMapMainLineIndex(WBSStore.selectedLine);
      await WBSStore.setSelectedLine(idx);
    }

    let actions: any = [];
    let selectedLine = WBSStore.selectedLine;
    let selectedLineData: any = WBSStore.WBSRows.find(
      (r) => r.id == WBSStore.selectedLine.id
    );
    let selectedLineRef = WBSStore.WBSRef.collection("rows").doc(
      selectedLine.id
    );
    let selectedLineRowIndex = selectedLine.data.rowIndex;
    let selectedLineRowOldParentStage = selectedLine.data.parentStage;

    if (to == "back") {
      // console.log("<<<   Voltar   >>>");
      let lineAbove: any = WBSStore.WBSRows.find(
        (r) => r.id == selectedLineRowOldParentStage
      );
      // Puxa as linhas da nova localização para baixo
      actions.push(
        ...this.changeRowIndex(
          lineAbove.data.rowIndex + 1,
          lineAbove.data.parentStage
        )
      );

      let allLinesAbove = WBSStore.WBSRows.filter(
        (r) =>
          selectedLineData &&
          r.data.rowIndex < selectedLineData.data.rowIndex &&
          r.data.parentStage == selectedLineRowOldParentStage &&
          r.id != selectedLineData.id
      );
      let allLinesBellow = WBSStore.WBSRows.filter(
        (r) =>
          selectedLineData &&
          r.data.rowIndex > selectedLineData.data.rowIndex &&
          r.data.parentStage == selectedLineRowOldParentStage &&
          r.id != selectedLineData.id
      );

      let upperLineTotal = 0;
      let upperLineTotalBdi = 0;
      const rules = WBSPropertiesStore.getRules(2);
      for (let i = 0; i < allLinesAbove.length; i++) {
        upperLineTotal = sum(
          upperLineTotal,
          allLinesAbove[i].data.total || 0,
          rules
        );
        upperLineTotalBdi = sum(
          upperLineTotalBdi,
          allLinesAbove[i].data.totalBdi || 0,
          rules
        );
      }
      let bellowLineTotal = 0;
      let bellowLineTotalBdi = 0;
      for (let i = 0; i < allLinesBellow.length; i++) {
        bellowLineTotal = sum(
          bellowLineTotal,
          allLinesBellow[i].data.total || 0,
          rules
        );
        bellowLineTotalBdi = sum(
          bellowLineTotalBdi,
          allLinesBellow[i].data.totalBdi || 0,
          rules
        );
      }
      // Transformar a linha acima de stage para resource se não houver mais linhas filhas
      if (allLinesAbove.length == 0) {
        let lineRef = WBSStore.WBSRef.collection("rows").doc(lineAbove.id);

        let defaultRow = WBSModels.getDefaultRow({
          rowType: ERowType.RESOURCE,
          rowIndex: lineAbove.data.rowIndex,
          description: lineAbove.data.description,
          total: 0,
        });

        defaultRow.parentStage = lineAbove.data.parentStage;
        let update = WBSService.removeUndefinedFieldsFromUpdatedRow(
          lineAbove,
          defaultRow
        );

        actions.push({
          type: "update",
          args: [lineRef, update],
          arrayChange: () => {
            if (lineAbove) {
              defaultRow.id = lineAbove.id;
              for (var key of Object.keys(update)) {
                if (
                  Object.keys(defaultRow).filter((d) => d == key).length == 0
                ) {
                  defaultRow[key] = undefined;
                }
              }
            }
          },
        });
      } else {
        actions.push(
          ...WBSStore.createActionsToUpdateParentRows(
            lineAbove.id,
            -subtract(lineAbove.data.total, upperLineTotal, rules),
            -subtract(lineAbove.data.totalBdi, upperLineTotalBdi, rules),
            false
          )
        );

        // Muda o indice da linha atual para a linha de cima + 1 e a referencia para a mesma da linha acima
        actions.push({
          type: "update",
          args: [
            selectedLineRef,
            {
              rowIndex: lineAbove.data.rowIndex + 1,
              parentStage: lineAbove.data.parentStage,
            },
          ],
        });
      }

      // Se houver linhas abaixo, a linha atual vira stage
      if (allLinesBellow.length > 0) {
        // Se houver linhas acima, atualiza
        let countChild = WBSStore.dataTable.filter(
          (dt) => dt.data.parentStage == selectedLine.id
        ).length;
        let a = -selectedLineRowIndex;
        let b = countChild - 1;
        let direction = a + b;
        actions.push(
          ...this.changeRowIndex(
            selectedLineRowIndex + 1,
            selectedLineRowOldParentStage,
            direction,
            selectedLine.id
          )
        );

        // Se for stage, mantem o valor do total anterior
        let oldTotal = 0;
        let oldTotalBdi = 0;
        if (selectedLine.data.rowType != ERowType.STAGE) {
          actions.push(
            ...WBSStore.createActionsToUpdateParentRows(
              lineAbove.data.parentStage,
              -selectedLine.data.total,
              -selectedLine.data.totalBdi
            )
          );
        } else {
          oldTotal = selectedLine.data.total;
          oldTotalBdi = selectedLine.data.totalBdi;
        }

        let defaultRow = WBSModels.getDefaultRow({
          rowType: ERowType.STAGE,
          rowIndex: lineAbove.data.rowIndex + 1,
          description: selectedLine.data.description,
          total: sum(bellowLineTotal, oldTotal, rules),
          totalBdi: sum(bellowLineTotalBdi, oldTotalBdi, rules),
        });
        defaultRow.parentStage = lineAbove.data.parentStage;

        if (selectedLineData.data.modelMapFull) {
          defaultRow.fromModelMap = selectedLineData.data.fromModelMap;
          defaultRow.modelMapFull = selectedLineData.data.modelMapFull;
        }

        // Muda a linha selecionada para stage
        let update = WBSService.removeUndefinedFieldsFromUpdatedRow(
          selectedLineData,
          defaultRow
        );
        actions.push({
          type: "update",
          args: [selectedLineRef, update],
          arrayChange: () => {
            if (selectedLineData) {
              defaultRow.id = selectedLineData.id;
              for (var key of Object.keys(update)) {
                if (
                  Object.keys(defaultRow).filter((d) => d == key).length == 0
                ) {
                  defaultRow[key] = undefined;
                }
              }
            }
          },
        });
      } else {
        // Puxa as linhas para baixo
        actions.push({
          type: "update",
          args: [
            selectedLineRef,
            {
              rowIndex: lineAbove.data.rowIndex + 1,
              parentStage: lineAbove.data.parentStage,
            },
          ],
        });
      }
    } else {
      // console.log("<<<   Avançar   >>>");
      let lineAbove: any = WBSStore.WBSRows.find(
        (r) =>
          r.data.fromModelMap == undefined &&
          r.data.rowIndex == selectedLineRowIndex - 1 &&
          r.data.parentStage == selectedLineRowOldParentStage &&
          r.data.rowType != ERowType.BLANK
      );

      if (lineAbove.data.rowType !== ERowType.STAGE) {
        // Puxa as linhas para cima
        actions.push(
          ...this.changeRowIndex(
            selectedLineRowIndex + 1,
            selectedLineRowOldParentStage,
            -1
          )
        );
        // Deleta recursivamente as linhas de dentro da task, caso seja
        actions.push(...WBSStore.recursiveRowDelete(lineAbove.id));

        if (lineAbove.data.total != undefined && lineAbove.data.total != 0) {
          actions.push(
            ...WBSStore.createActionsToUpdateParentRows(
              lineAbove.data.parentStage,
              -lineAbove.data.total,
              -lineAbove.data.totalBdi
            )
          );
        }

        // Muda o contentId para um novo, a linha não estará mais ligada a outras linhas.
        let lineRef = WBSStore.WBSRef.collection("rows").doc(lineAbove.id);
        let defaultRow = WBSModels.getDefaultRow({
          rowType: ERowType.STAGE,
          rowIndex: lineAbove.data.rowIndex,
          description: lineAbove.data.description,
          total: selectedLine.data.total,
          totalBdi: selectedLine.data.totalBdi,
          customProperties: lineAbove.data.customProperties,
        });

        defaultRow.parentStage = lineAbove.data.parentStage;

        // Muda a linha acima para stage
        let update = WBSService.removeUndefinedFieldsFromUpdatedRow(
          lineAbove,
          defaultRow
        );
        actions.push({
          type: "update",
          args: [lineRef, update],
          arrayChange: () => {
            if (lineAbove) {
              defaultRow.id = lineAbove.id;
              for (var key of Object.keys(update)) {
                if (
                  Object.keys(defaultRow).filter((d) => d == key).length == 0
                ) {
                  defaultRow[key] = undefined;
                }
              }
            }
          },
        });

        // Muda o indice da linha atual para 0 e a referencia para a linha acima
        actions.push({
          type: "update",
          args: [selectedLineRef, { rowIndex: 0, parentStage: lineAbove.id }],
        });
      } else {
        // Puxa as linhas para cima
        actions.push(
          ...this.changeRowIndex(
            selectedLineRowIndex + 1,
            selectedLineRowOldParentStage,
            -1
          )
        );

        actions.push(
          ...WBSStore.createActionsToUpdateParentRows(
            lineAbove.id,
            selectedLineData.data.total,
            selectedLineData.data.totalBdi,
            false
          )
        );

        let childrenCount = WBSStore.WBSRows.filter(
          (r) => lineAbove && r.data.parentStage == lineAbove.id
        );
        if (childrenCount) {
          // Muda o indice da linha para a ultima linha do item acima (o número de itens que ela tem de filhos) e a referencia para a linha acima
          actions.push({
            type: "update",
            args: [
              selectedLineRef,
              { rowIndex: childrenCount.length, parentStage: lineAbove.id },
            ],
          });
        }
      }
    }
    await WBSStore.batchActions(actions, true, () => {
      WBSStore.loadDatatableWithRightFormat(WBSStore.WBSRows);
      setTimeout(() => {
        WBSStore.setSelectedLine(selectedLine.index, "description");
      }, 500);
    });
  }

  @action
  async moveWBSRow(to = "up") {
    if (WBSStore.selectedLine.data.modelMapFull) {
      let idx = this.findModelMapMainLineIndex(WBSStore.selectedLine);
      await WBSStore.setSelectedLine(idx);
    }
    let selectedLine = WBSStore.selectedLine;
    let actions = new Array();
    // let newIndex = selectedLine.index + ;
    let oldDt = WBSStore.dataTable[selectedLine.index];
    let newIndex = WBSStore.dataTable.findIndex((d) => {
      return (
        d.data.rowIndex == oldDt.data.rowIndex + (to === "up" ? -1 : 1) &&
        d.data.parentStage == oldDt.data.parentStage
      );
    });
    if (newIndex == -1) return;
    let newDt = WBSStore.dataTable[newIndex];
    let oldIndexInStage = oldDt.data.rowIndex;
    let newIndexInStage = newDt.data.rowIndex;

    if (
      oldDt.data.rowType == ERowType.BLANK &&
      newDt.data.rowType == ERowType.BLANK
    ) {
      setTimeout(() => {
        WBSStore.setSelectedLine(newIndex, selectedLine.dataKey, true);
      }, 500);
      return;
    }

    let wbsRowsRef = WBSStore.WBSRef.collection("rows");

    actions.push({
      type: "update",
      args: [wbsRowsRef.doc(oldDt.id), { rowIndex: newIndexInStage }],
    });

    actions.push({
      type: "update",
      args: [wbsRowsRef.doc(newDt.id), { rowIndex: oldIndexInStage }],
    });

    await WBSStore.batchActions(actions, true, () => {
      WBSStore.loadDatatableWithRightFormat(WBSStore.WBSRows);
      let newIndexToSelect = WBSStore.dataTable.findIndex(
        (dt) =>
          dt.data.parentStage == newDt.data.parentStage &&
          dt.data.rowIndex == newIndexInStage
      );
      setTimeout(() => {
        WBSStore.setSelectedLine(newIndexToSelect, selectedLine.dataKey, true);
      }, 500);
    });
  }
}

let ToolbarStore = new Store();

export { ToolbarStore };



// WEBPACK FOOTER //
// ./src/modules/WBS/ToolbarStore.tsx